export enum LabParameterGroupId {
  // Human / adult & child groups
  CBC = "CBC",
  LIPID = "LIPID",
  SUGAR = "SUGAR",
  THYROID = "THYROID",
  LFT = "LFT",
  KFT = "KFT",
  VITMIN = "VITMIN",
  IRON = "IRON",
  HORMONES = "HORMONES",
  INFLAMM = "INFLAMM",
  OTHERS = "OTHERS",
  // Pet (dog/cat) groups
  CBC_PET = "CBC_PET",
  KFT_PET = "KFT_PET",
  URINE_PET = "URINE_PET",
  LFT_PET = "LFT_PET",
  SUGAR_PET = "SUGAR_PET",
  THYROID_PET = "THYROID_PET",
  OTHERS_PET = "OTHERS_PET",
}

export enum RangeKind {
  NUMERIC = "numeric",
  CATEGORICAL = "categorical",
  COMPOUND = "compound",
  DESCRIPTIVE = "descriptive",
}

export enum RangeComparator {
  LESS_THAN = "<",
  GREATER_THAN = ">",
}

export enum ParameterStatus {
  NORMAL = "normal",
  LOW = "low",
  HIGH = "high",
  BORDERLINE_LOW = "borderline-low",
  BORDERLINE_HIGH = "borderline-high",
  UNKNOWN = "unknown",
}

export enum PinSource {
  AUTO = "auto",
  USER = "user",
}

export enum PinReason {
  CHRONIC_CONDITION = "chronic_condition",
  OUTLIER = "outlier",
  FALLBACK = "fallback",
}

export type RangeSource = "lab" | "taxonomy" | "none";

export interface LatestReading {
  parsedResult: number | null;
  rawResult: string | null;
  unit: string | null;
  status: ParameterStatus;
  flagged: boolean;
  rangeText: string | null;
  rangeLow: number | null;
  rangeHigh: number | null;
  rangeComparator: RangeComparator | null;
  rangeKind: RangeKind;
  rangeSource: RangeSource;
  deviationScore: number | null;
  reportDate: string | null;
  labName: string | null;
  reportId: string;
}

export interface PinnedParameterCard {
  canonicalParameterId: string;
  canonicalName: string;
  groupId: LabParameterGroupId;
  groupName: string;
  pinnedBy: PinSource;
  pinnedReason: PinReason | null;
  latestReading: LatestReading | null;
}

export interface ConstellationGroup {
  groupId: LabParameterGroupId;
  groupName: string;
  active: boolean;
  flaggedCount: number;
  statusLabel: string;
}

export interface LabInsightsLanding {
  pinnedParameters: PinnedParameterCard[];
  constellation: ConstellationGroup[];
  mostRecentReportDate: string | null;
  mostRecentLabName: string | null;
}

export interface LabInsightsAllGroups {
  groups: ConstellationGroup[];
}

export interface GroupParameterCard {
  /** Null for unmatched parameters in the Others bucket — drill-down disabled. */
  canonicalParameterId: string | null;
  canonicalName: string;
  latestReading: LatestReading | null;
}

export interface LabInsightsGroupDetails {
  groupId: LabParameterGroupId;
  groupName: string;
  mostRecentLabName: string | null;
  mostRecentReportDate: string | null;
  active: boolean;
  parameters: GroupParameterCard[];
  tabs: ConstellationGroup[];
}

export interface RangeBar {
  parsedResult: number | null;
  rawResult: string | null;
  unit: string | null;
  status: ParameterStatus;
  flagged: boolean;
  rangeText: string | null;
  rangeLow: number | null;
  rangeHigh: number | null;
  rangeComparator: RangeComparator | null;
  rangeKind: RangeKind;
}

export interface TrendSummary {
  direction: "up" | "down" | "stable" | "mixed" | "insufficient";
  text: string;
  readingsCount: number;
}

export interface HistoricalReading {
  reportId: string;
  reportDate: string | null;
  labName: string | null;
  parsedResult: number | null;
  rawResult: string | null;
  unit: string | null;
  status: ParameterStatus;
  flagged: boolean;
  rangeText: string | null;
  rangeLow: number | null;
  rangeHigh: number | null;
  rangeComparator: RangeComparator | null;
  deviationScore: number | null;
}

export interface LabInsightsParameterDetails {
  canonicalParameterId: string;
  canonicalName: string;
  groupId: LabParameterGroupId;
  groupName: string;
  patientDescription: string | null;
  rangeBar: RangeBar | null;
  interpretation: string | null;
  trendSummary: TrendSummary | null;
  history: HistoricalReading[];
}

export interface SetPinnedParametersRequest {
  canonicalParameterIds: string[];
}

export interface PinnableParameter {
  canonicalParameterId: string;
  canonicalName: string;
  groupId: LabParameterGroupId;
  groupName: string;
}

export interface PinnableParameters {
  parameters: PinnableParameter[];
}
